import { useEffect, useMemo, useState } from 'react';

import { PermissionRoles, WorkspaceSwitcherCreateParamsKind } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { GearbeltLoader } from '@gearbelt/ui/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Countries, IAccount, TimezoneMapping } from '@site-mate/sitemate-global-shared';
import { useDebounce } from '@uidotdev/usehooks';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { ApiService } from '@/services/api-v2';
import { accountService } from '@/services/global-api/account.service';
import { workspaceService } from '@/services/global-api/workspace.service';
import { useUserStore } from '@/stores/user.store';

import { Form } from '../ui/form';
import { useToast } from '../ui/toaster';
import {
  AccountSubdomainInputField,
  CreateWorkspaceAsRegularUserSchema,
  CreateWorkspaceAsRegularUserSchemaType,
  CreateWorkspaceInputField,
  CreateWorkspaceInputFieldWithTooltip,
  CreateWorkspaceSchema,
  CreateWorkspaceSchemaType,
  CreateWorkspaceSelectField,
  WorkspaceNameInputField,
} from './create-workspace-form';

async function validateWorkspaceName(workspaceName: string): Promise<string | null> {
  try {
    const { isValidWorkspaceName } = await workspaceService.checkForBannedOrReservedWords(workspaceName.toLowerCase());
    if (!isValidWorkspaceName) {
      return 'Workspace names cannot contain inappropriate or reserved terms. Please use a different name.';
    }
    return null;
  } catch (err) {
    const isBannedNameError =
      err !== null && typeof err === 'object' && 'response' in err && (err as { response: { status: number } }).response?.status === 400;
    return isBannedNameError
      ? 'Workspace names cannot contain inappropriate or reserved terms. Please use a different name.'
      : 'Could not validate workspace name. Please try again.';
  }
}

const CreateWorkspaceAsRegularUser = ({ account }: { account?: IAccount | null }) => {
  const toast = useToast();
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const { user } = useUserStore();

  const getContinentFromTimezone = (timezone: string): string => {
    const userCountry = TimezoneMapping[timezone]?.countries[0]?.code?.toLowerCase();
    const continentByTimezone = Countries.find((c) => c.ccode.toLowerCase() === userCountry)?.continentCode;
    return continentByTimezone ?? 'OC';
  };

  const form = useForm<CreateWorkspaceAsRegularUserSchemaType>({
    resolver: zodResolver(CreateWorkspaceAsRegularUserSchema),
    defaultValues: {
      workspaceName: '',
    },
    mode: 'onChange',
  });

  async function onSubmit(data: CreateWorkspaceAsRegularUserSchemaType) {
    setIsCreatingWorkspace(true);

    const workspaceNameError = await validateWorkspaceName(data.workspaceName);
    if (workspaceNameError) {
      form.setError('workspaceName', { type: 'manual', message: workspaceNameError });
      setIsCreatingWorkspace(false);
      return;
    }

    try {
      const timezone = user?.timezone || dayjs.tz.guess();
      const {
        workspace: { workspaceId },
      } = await ApiService.CreateWorkspace({
        workspaceName: data.workspaceName,
        accountId: account?._id,
        workspaceSwitcherParams: {
          kind: WorkspaceSwitcherCreateParamsKind.UseExistingAccount,
        },
        continent: account?.region || getContinentFromTimezone(timezone),
        timezone,
      }).then((res) => res.json());

      const pathname = `/workspaces/${workspaceId}/dashboard`;
      window.location.href = pathname;
    } catch (err) {
      setIsCreatingWorkspace(false);
      console.error(err);
      toast.error('Failed to create workspace');
    }
  }

  const isSubmitButtonDisabled = useMemo(() => {
    return !form.formState.isValid || form.formState.isSubmitting || Object.keys(form.formState.errors).length > 0 || isCreatingWorkspace;
  }, [form.formState.isValid, form.formState.isSubmitting, form.formState.errors, isCreatingWorkspace]);

  if (!account) {
    return (
      <div className="mx-4 flex items-center justify-center">
        <span>SMS Account is invalid.</span>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-y-4">
        <WorkspaceNameInputField name="workspaceName" control={form.control} required />

        <div className="text-secondary-text flex flex-col text-xs">
          <span>The new workspace will be linked to the same Sitemate Start Account.</span>
          <span>Sitemate Start Account ID: {account._id}</span>
        </div>

        <Button type="submit" variant="success" className="self-start p-2" disabled={isSubmitButtonDisabled} loading={form.formState.isSubmitting}>
          Create Workspace
        </Button>
      </form>
    </Form>
  );
};

const CreateWorkspaceAsSuperuser = ({ account }: { account?: IAccount | null }) => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const accountIdParam = searchParams.get('accountId');

  const defaultKind =
    accountIdParam || account?._id ? WorkspaceSwitcherCreateParamsKind.UseExistingAccount : WorkspaceSwitcherCreateParamsKind.CreateNewAccount;

  const getContinentFromTimezone = (timezone: string): string => {
    const userCountry = TimezoneMapping[timezone]?.countries[0]?.code?.toLowerCase();
    const continentByTimezone = Countries.find((c) => c.ccode.toLowerCase() === userCountry)?.continentCode;
    return continentByTimezone ?? 'OC';
  };

  const form = useForm<CreateWorkspaceSchemaType>({
    resolver: zodResolver(CreateWorkspaceSchema),
    defaultValues: {
      kind: defaultKind,
      workspaceName: '',
      accountId: accountIdParam ?? account?._id,
      accountName: account?.name,
      accountSubdomain: '',
    },
    mode: 'onChange',
  });
  const formKind = form.watch('kind');
  const workspaceName = form.watch('workspaceName');
  const accountId = form.watch('accountId');
  const debouncedAccountId = useDebounce(accountId, 500);
  const isAccountUkRegion = account?.region === 'uk1';
  const [isValidAccountId, setIsValidAccountId] = useState(
    defaultKind === WorkspaceSwitcherCreateParamsKind.CreateNewAccount || (!!account?._id && !isAccountUkRegion),
  );
  const [fetchedAccount, setFetchedAccount] = useState<IAccount | null>(account ?? null);

  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);

  useEffect(() => {
    if (isAccountUkRegion) {
      form.setError('accountId', {
        message: 'You cannot link to a Sitemate Start Account that is set to a different data region.',
        type: 'manual',
      });
    }
  }, [isAccountUkRegion, form]);

  useEffect(() => {
    if (formKind === WorkspaceSwitcherCreateParamsKind.UseExistingAccount) {
      if (debouncedAccountId && debouncedAccountId !== fetchedAccount?._id) {
        accountService
          .getAccountById(debouncedAccountId)
          .then((account) => {
            setFetchedAccount(account);
            if (account?.region === 'uk1') {
              form.setValue('accountName', account?.name ?? '', { shouldValidate: true });
              setIsValidAccountId(false);
              form.setError('accountId', {
                message: 'You cannot link to a Sitemate Start Account that is set to a different data region.',
                type: 'manual',
              });
              return;
            }
            form.clearErrors('accountId');
            form.setValue('accountName', account?.name ?? '', { shouldValidate: true });
            setIsValidAccountId(true);
          })
          .catch(() => {
            setFetchedAccount(null);
            form.setValue('accountName', '');
            setIsValidAccountId(false);
            form.setError('accountId', { message: 'Sitemate Start Account could not be found with this ID.', type: 'manual' });
          });
      }
    } else if (formKind === WorkspaceSwitcherCreateParamsKind.CreateNewAccount) {
      form.setValue('accountRegion', 'au1', { shouldValidate: true });
      form.setValue('accountName', workspaceName || '', { shouldValidate: true });
      setIsValidAccountId(true);
    }
  }, [debouncedAccountId, fetchedAccount, form, formKind, workspaceName]);

  const WhereToAddWorkspaceOptions: Array<{ label: string; value: WorkspaceSwitcherCreateParamsKind }> = [
    {
      label: 'Add this workspace to an existing Sitemate Start Account',
      value: WorkspaceSwitcherCreateParamsKind.UseExistingAccount,
    },
    {
      label: 'Create a new Sitemate Start Account for this workspace',
      value: WorkspaceSwitcherCreateParamsKind.CreateNewAccount,
    },
  ];

  const AccountRegionOptions: Array<{ label: string; value: string }> = [
    {
      label: '🇦🇺 Australia',
      value: 'au1',
    },
  ];

  const submitButtonLabel = useMemo(() => {
    switch (formKind) {
      case WorkspaceSwitcherCreateParamsKind.UseExistingAccount:
        return 'Create Workspace';
      case WorkspaceSwitcherCreateParamsKind.CreateNewAccount:
        return 'Create Sitemate Start Account and Workspace';
    }
  }, [formKind]);

  const isSubmitButtonDisabled = useMemo(() => {
    return (
      !form.formState.isValid ||
      form.formState.isSubmitting ||
      !isValidAccountId ||
      Object.keys(form.formState.errors).length > 0 ||
      isCreatingWorkspace ||
      form.formState.isValidating
    );
  }, [form.formState.isValid, form.formState.isSubmitting, isValidAccountId, form.formState.errors, isCreatingWorkspace, form.formState.isValidating]);

  async function onSubmit(data: CreateWorkspaceSchemaType) {
    setIsCreatingWorkspace(true);

    const workspaceNameError = await validateWorkspaceName(data.workspaceName);
    if (workspaceNameError) {
      form.setError('workspaceName', { type: 'manual', message: workspaceNameError });
      setIsCreatingWorkspace(false);
      return;
    }

    try {
      const timezone = dayjs.tz.guess();
      const params: Parameters<typeof ApiService.CreateWorkspace>[0] = {
        workspaceName: data.workspaceName,
        continent: getContinentFromTimezone(timezone),
        timezone,
      };

      if (data.kind === WorkspaceSwitcherCreateParamsKind.UseExistingAccount) {
        params.accountId = data.accountId;
        params.workspaceSwitcherParams = {
          kind: WorkspaceSwitcherCreateParamsKind.UseExistingAccount,
        };
      } else if (data.kind === WorkspaceSwitcherCreateParamsKind.CreateNewAccount) {
        params.workspaceSwitcherParams = {
          kind: WorkspaceSwitcherCreateParamsKind.CreateNewAccount,
          // TODO: Sync with SMS Team to centralize region definitions.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          accountRegion: data.accountRegion as any,
          accountSubdomain: data.accountSubdomain,
        };
      }

      const {
        workspace: { workspaceId },
      } = await ApiService.CreateWorkspace(params).then((res) => res.json());

      const pathname = `/workspaces/${workspaceId}/dashboard`;
      window.location.href = pathname;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      toast.error('Failed to create workspace');
      setIsCreatingWorkspace(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-y-4">
        <CreateWorkspaceSelectField
          label="Where would you like to add this workspace?"
          name="kind"
          control={form.control}
          options={WhereToAddWorkspaceOptions}
        />

        <WorkspaceNameInputField name="workspaceName" control={form.control} required />

        {formKind === WorkspaceSwitcherCreateParamsKind.UseExistingAccount && (
          <>
            <CreateWorkspaceInputFieldWithTooltip
              label="Sitemate Start Account ID"
              name="accountId"
              control={form.control}
              placeholder="Text..."
              required
              tooltipContent={
                <div className="w-[270px] text-sm">
                  <div className="mb-4">You can find the Sitemate Start Account ID of a customer in the workspace settings of any of their workspaces.</div>
                  <div>It will look like this: acc_XXXXXXXX</div>
                </div>
              }
              tooltipContentProps={{
                side: 'right',
              }}
            />
          </>
        )}

        <div className="flex flex-col gap-y-1">
          {formKind === WorkspaceSwitcherCreateParamsKind.UseExistingAccount && (
            <>
              <CreateWorkspaceInputField
                label="Sitemate Start Account Name"
                name="accountName"
                control={form.control}
                placeholder="Text..."
                required
                disabled
              />
              <span className="text-secondary-text text-xs">This name will appear once you populate the Sitemate Start Account ID.</span>
            </>
          )}
          {formKind === WorkspaceSwitcherCreateParamsKind.CreateNewAccount && (
            <>
              <CreateWorkspaceInputField
                label="Sitemate Start Account Name"
                name="workspaceName"
                control={form.control}
                placeholder="Text..."
                disabled
                noErrorMessage
              />
              <span className="text-secondary-text text-xs">The Sitemate Start Account name will match Workspace name.</span>
            </>
          )}
        </div>

        {formKind === WorkspaceSwitcherCreateParamsKind.CreateNewAccount && (
          <>
            <CreateWorkspaceSelectField
              label="Sitemate Start Account Region"
              name="accountRegion"
              control={form.control}
              options={AccountRegionOptions}
              required
            />
            <AccountSubdomainInputField name="accountSubdomain" control={form.control} required />
          </>
        )}

        <Button type="submit" variant="success" className="self-start p-2" disabled={isSubmitButtonDisabled} loading={form.formState.isSubmitting}>
          {submitButtonLabel}
        </Button>
      </form>
    </Form>
  );
};

const CreateWorkspace = ({ account, isLoading }: { account?: IAccount | null; isLoading?: boolean }) => {
  const { user } = useUserStore();

  const isSuperuser = useMemo(() => user?.roles.includes(PermissionRoles.SitemateSuperUser) ?? false, [user?.roles]);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <GearbeltLoader size={30} />
      </div>
    );
  }

  return isSuperuser ? <CreateWorkspaceAsSuperuser account={account} /> : <CreateWorkspaceAsRegularUser account={account} />;
};

export default CreateWorkspace;
